"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, Phone, Mail, Clock, Calendar, CircleCheck, CircleAlert, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const AppointmentForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center space-y-6 bg-card/50 border border-border rounded-lg animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-green-500/20 flex items-center justify-center">
          <CircleCheck className="size-8 text-green-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-semibold text-foreground">Randevunuz Alındı</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Yeni Randevu Oluştur
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Lütfen adınızı ve soyadınızı giriniz."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Adınız Soyadınız</FormLabel>
              <FormControl>
                <Input placeholder="Ahmet Yılmaz" {...field} className="bg-background/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Lütfen telefon numaranızı giriniz."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Telefon Numarası</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+90 555 123 4567" {...field} className="bg-background/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Lütfen e-posta adresinizi giriniz.",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Geçerli bir e-posta adresi giriniz."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">E-posta Adresi</FormLabel>
            <FormControl>
              <Input type="email" placeholder="ahmet@example.com" {...field} className="bg-background/50" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="preferred_date" rules={{
        required: "Lütfen bir tarih seçiniz."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Tercih Edilen Tarih</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="date" {...field} className="bg-background/50" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_time" rules={{
        required: "Lütfen bir saat aralığı seçiniz."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Tercih Edilen Saat</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full rounded-md border border-input bg-background/50 px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                  <option value="" disabled>Saati seçiniz</option>
                  <option value="09:00 - 11:00">09:00 - 11:00</option>
                  <option value="11:00 - 13:00">11:00 - 13:00</option>
                  <option value="13:00 - 15:00">13:00 - 15:00</option>
                  <option value="15:00 - 17:00">15:00 - 17:00</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="purpose" rules={{
      required: "Lütfen randevu konusunu seçiniz."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Randevu Konusu</FormLabel>
            <FormControl>
              <select {...field} className="flex h-9 w-full rounded-md border border-input bg-background/50 px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                <option value="" disabled>Konu seçiniz</option>
                <option value="Ceza Hukuku Danışmanlığı">Ceza Hukuku Danışmanlığı</option>
                <option value="Aile ve Boşanma Hukuku">Aile ve Boşanma Hukuku</option>
                <option value="Kurumsal Hukuki Danışmanlık">Kurumsal Hukuki Danışmanlık</option>
                <option value="Şirketler Hukuku">Şirketler Hukuku</option>
                <option value="Gayrimenkul ve İnşaat Sözleşmeleri">Gayrimenkul ve İnşaat Sözleşmeleri</option>
                <option value="İşçi-İşveren Uyuşmazlıkları">İşçi-İşveren Uyuşmazlıkları</option>
                <option value="Sigorta ve Trafik Kazası Tazminatı">Sigorta ve Trafik Kazası Tazminatı</option>
                <option value="İdari Dava">İdari Dava</option>
                <option value="İcra Hukuku">İcra Hukuku</option>
                <option value="Diğer">Diğer</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Ek Bilgi / Not</FormLabel>
            <FormControl>
              <Textarea placeholder="Görüşme öncesi eklemek istediğiniz notları buraya yazabilirsiniz." className="min-h-[100px] bg-background/50 resize-none" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              Bu alan zorunlu değildir.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-slate-700 hover:bg-slate-600 text-white font-medium py-6 text-base shadow-lg shadow-slate-900/20 transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Gönderiliyor...
          </> : "Randevu Talebi Oluştur"}
      </Button>
    </Form>;
};
export default function OfisLokasyonuVeRandevu() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="ofis-lokasyonu-ve-randevu" ref={sectionRef} lang="tr" className="dark bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900/40 via-background to-background pointer-events-none" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-12 xl:gap-16 shadow-2xl rounded-2xl overflow-hidden border border-border/40 bg-card transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {}
          <div className="relative h-[400px] lg:h-auto min-h-[500px] bg-slate-950 flex flex-col">
            {}
            <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80 lg:opacity-60" />
            
            {}
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3145.748680045844!2d30.5543187!3d37.7663248!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c5b5071987677d%3A0x7d6c66d1f0e2f50!2sKutlubey%2C%201006.%20Sk.%20No%3A31%2C%2032100%20Isparta%20Merkez%2FIsparta!5e0!3m2!1str!2str!4v1715850000000!5m2!1str!2str" width="100%" height="100%" style={{
            border: 0,
            filter: "grayscale(100%) invert(92%) contrast(83%) brightness(90%) hue-rotate(180deg)"
          }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 w-full h-full object-cover opacity-80" title="Ofis Konumu" />

            {}
            <div className="relative z-20 mt-auto p-8 lg:p-10 space-y-6 bg-slate-950/80 backdrop-blur-sm border-t border-white/10">
              <div className="space-y-2">
                <h3 className="text-2xl font-serif font-bold text-white">İletişim Bilgileri</h3>
                <p className="text-slate-400 text-sm">Ofisimize ulaşmak için aşağıdaki kanalları kullanabilirsiniz.</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 group">
                  <div className="p-2.5 rounded-lg bg-slate-800/50 text-slate-300 group-hover:bg-slate-700/50 group-hover:text-white transition-colors">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-0.5">Adres</p>
                    <p className="text-slate-200 text-sm leading-relaxed">Avukat & Arabulucu Muhammed Çelik</p>
                    <p className="text-slate-200 text-sm leading-relaxed">
                      Kutlubey Mah. Cengiz Topel Cad. 1006 Sk. No:31/1 Merkez/ISPARTA
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="p-2.5 rounded-lg bg-slate-800/50 text-slate-300 group-hover:bg-slate-700/50 group-hover:text-white transition-colors">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-0.5">Telefon</p>
                    <p className="text-slate-200 text-sm">+90 554 842 15 37</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="p-2.5 rounded-lg bg-slate-800/50 text-slate-300 group-hover:bg-slate-700/50 group-hover:text-white transition-colors">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-0.5">E-posta</p>
                    <p className="text-slate-200 text-sm">av.celik@mail.com</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 group">
                  <div className="p-2.5 rounded-lg bg-slate-800/50 text-slate-300 group-hover:bg-slate-700/50 group-hover:text-white transition-colors">
                    <Clock className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-0.5">Çalışma Saatleri</p>
                    <p className="text-slate-200 text-sm">Pazartesi - Cuma: 09:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="p-8 lg:p-12 bg-card flex flex-col justify-center">
            <div className="mb-8 space-y-2">
              <div className="flex items-center gap-2 text-primary mb-2">
                <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                <span className="text-sm font-semibold uppercase tracking-widest">Randevu Alın</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground">
                Ofis Lokasyonu ve Randevu
              </h2>
              <p className="text-muted-foreground">
                Hukuki danışmanlık ve görüşme talepleriniz için aşağıdaki formu doldurarak bize ulaşabilirsiniz. En kısa sürede size dönüş yapılacaktır.
              </p>
            </div>

            <FormProvider formId="appointment_form_contact" sectionName="ofis-lokasyonu-ve-randevu" defaultValues={{
            name: "",
            email: "",
            phone: "",
            preferred_date: "",
            preferred_time: "",
            purpose: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            preferred_date: {
              required: true
            },
            preferred_time: {
              required: true
            },
            purpose: {
              required: true
            }
          }} submitText="Randevu Talebi Oluştur" successMessage="Randevu talebiniz başarıyla alınmıştır. En kısa sürede sizinle iletişime geçeceğiz." errorMessage="Form gönderilirken bir hata oluştu. Lütfen tekrar deneyiniz." className="w-full">
              <AppointmentForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}